/**
 * documentConstants — client-side mirror of the CRM document storage limits.
 *
 * AUTHORITATIVE SOURCE: functions/CRM/documents/documentsShared.js. These
 * values exist only for early validation and nicer UX (instant rejection of
 * oversized/disallowed files before a network round-trip). The callable
 * re-validates everything server-side and the private bucket enforces
 * file_size_limit + allowed_mime_types as the final backstop.
 */

/** Private Supabase Storage bucket. Uploads go direct via signed URLs. */
export const DOCUMENTS_BUCKET = 'crm-documents';

/** 25 MB — keep in sync with documentsShared.MAX_FILE_SIZE. */
export const MAX_FILE_SIZE = 25 * 1024 * 1024;

/** MIME → extensions. Keep in sync with documentsShared.ALLOWED_MIME_TYPES. */
export const ALLOWED_MIME_TYPES = {
  'application/pdf': ['pdf'],
  'application/msword': ['doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx'],
  'application/vnd.ms-excel': ['xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
  'application/vnd.ms-powerpoint': ['ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['pptx'],
  'text/plain': ['txt'],
  'text/csv': ['csv'],
  'application/rtf': ['rtf'],
  'text/rtf': ['rtf'],
  'image/png': ['png'],
  'image/jpeg': ['jpg', 'jpeg'],
  'image/webp': ['webp'],
  'image/heic': ['heic'],
  'image/heif': ['heif'],
};

/** Extension → MIME fallback for browsers that report an empty/unknown type. */
export const EXTENSION_TO_MIME = Object.entries(ALLOWED_MIME_TYPES).reduce((acc, [mime, exts]) => {
  exts.forEach((ext) => {
    if (!acc[ext]) acc[ext] = mime;
  });
  return acc;
}, {});

/** value for <input type="file" accept="..."> */
export const FILE_INPUT_ACCEPT = [
  ...Object.keys(ALLOWED_MIME_TYPES),
  ...Object.values(ALLOWED_MIME_TYPES)
    .flat()
    .map((ext) => `.${ext}`),
].join(',');

/** MIME types the panel offers "View" (inline browser tab) for. */
export const INLINE_VIEWABLE_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/plain',
];

/** Matches the CHECK constraint on crm_documents.category. */
export const DOCUMENT_CATEGORIES = [
  { value: 'general', label: 'General', color: 'gray' },
  { value: 'resume', label: 'Resume', color: 'purple' },
  { value: 'license', label: 'License', color: 'blue' },
  { value: 'agreement', label: 'Agreement', color: 'green' },
  { value: 'other', label: 'Other', color: 'orange' },
];

export function getCategoryMeta(value) {
  return DOCUMENT_CATEGORIES.find((c) => c.value === value) || DOCUMENT_CATEGORIES[0];
}

/** Lowercased extension (without dot) or '' when the name has none. */
export function getExtension(fileName) {
  const name = String(fileName || '');
  const idx = name.lastIndexOf('.');
  if (idx <= 0 || idx === name.length - 1) return '';
  return name.slice(idx + 1).toLowerCase();
}

/**
 * Coarse file kind for icon/color mapping in the UI.
 * Returns 'pdf' | 'word' | 'excel' | 'powerpoint' | 'image' | 'text' | 'other'.
 */
export function getFileKind(mimeType, fileName) {
  const mime = String(mimeType || '').toLowerCase();
  const ext = getExtension(fileName);
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.startsWith('image/')) return 'image';
  if (mime.includes('word') || ['doc', 'docx', 'rtf'].includes(ext)) return 'word';
  if (mime.includes('sheet') || mime.includes('excel') || mime === 'text/csv' || ['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
  if (mime.includes('presentation') || mime.includes('powerpoint') || ['ppt', 'pptx'].includes(ext)) return 'powerpoint';
  if (mime.startsWith('text/') || ext === 'txt') return 'text';
  return 'other';
}

/**
 * Which in-page renderer the DocumentViewerModal uses.
 * Returns 'pdf' | 'image' | 'docx' | 'sheet' | 'text' | 'none'.
 * 'none' (doc, ppt, pptx, rtf) renders the FallbackViewer card — no reliable
 * client-side renderer exists and third-party embed viewers would leak
 * signed URLs to external services.
 */
export function getViewerType(mimeType, fileName) {
  const mime = String(mimeType || '').toLowerCase();
  const ext = getExtension(fileName);
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif'].includes(ext)) return 'image';
  if (
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    ext === 'docx'
  ) {
    return 'docx';
  }
  if (
    [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ].includes(mime) ||
    ['xls', 'xlsx', 'csv'].includes(ext)
  ) {
    return 'sheet';
  }
  if (mime === 'text/plain' || ext === 'txt') return 'text';
  return 'none';
}

export function formatFileSize(bytes) {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return '0 B';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Client-side pre-check mirroring the server's validateUploadRequest.
 * Returns { ok: true } or { ok: false, error }.
 */
export function validateFileForUpload(file) {
  if (!file || !file.name) {
    return { ok: false, error: 'Invalid file' };
  }
  if (!file.size || file.size <= 0) {
    return { ok: false, error: 'File is empty' };
  }
  if (file.size > MAX_FILE_SIZE) {
    return { ok: false, error: `Exceeds the ${Math.round(MAX_FILE_SIZE / (1024 * 1024))} MB limit` };
  }
  const mime = String(file.type || '').toLowerCase();
  const allowedByMime = !!ALLOWED_MIME_TYPES[mime];
  const allowedByExt = !!EXTENSION_TO_MIME[getExtension(file.name)];
  if (!allowedByMime && !allowedByExt) {
    return { ok: false, error: 'File type not allowed' };
  }
  return { ok: true };
}
